import { mergeDeep } from '@givebacks/utils/objects';
import { PrimitiveAtom, atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

import { SearchInstanceAtom, TableSearchObject } from './types';

// Atom that hold a list of unique id's
const tableSearchListAtom = atom<string[]>([]);
tableSearchListAtom.debugLabel = 'tableSearchListAtom';

// Family that provides unique atom references via id
const tableSearchAtomFamily = atomFamily(
  (args: Partial<SearchInstanceAtom>): PrimitiveAtom<Partial<SearchInstanceAtom>> => atom(args),
  (itemA, itemB) => itemA.id === itemB.id
);

// List of atoms from our `tableSearchListAtom`
const tableSearchAtomsAtom = atom((get) => {
  const listAllAtomIds = get(tableSearchListAtom);
  return listAllAtomIds.map((id) => get(tableSearchAtomFamily({ id })));
});

tableSearchAtomsAtom.debugLabel = 'tableSearchAtomsAtom';

const tableSearchResetAtom = atom(null, (get) => {
  const listAllAtomIds = get(tableSearchAtomsAtom);
  listAllAtomIds.forEach((search) => {
    return search.reset?.();
  });
});

/** Allows resetting Atoms by using a field, any atom with that field in their `fields` will have reset called against it  */
const tableSearch_InputResetAtom = atom(null, (get, _set, field: string) => {
  const listAllAtomIds = get(tableSearchAtomsAtom).filter((atom) => atom?.fields?.includes(field));
  listAllAtomIds.forEach((search) => {
    return search.reset?.();
  });
});

/** Allows resetting all search inputs that are not shown in params  */
const tableSearch_InputResetNotShownInParamsAtom = atom(null, (get, _set, field: string) => {
  const listAllAtomIds = get(tableSearchAtomsAtom).filter((atom) => atom?.showInParams === false);
  listAllAtomIds.forEach((search) => {
    return search.reset?.();
  });
});

/**
 * Allows setting an inputs value by using a field name, any atom with that field in their `fields` list will have their onSet called
 *  Optionally can exclude fields that are not shown in params
 */
const tableSearch_InputFormatterAtom = atom(
  null,
  (get, _set, { field, value }: { field: string; value: string }) => {
    const listAllAtomIds = get(tableSearchAtomsAtom).filter(
      (atom) => atom?.fields?.includes(field)
    );

    const firstMatchingAtom = listAllAtomIds.find((atom) => atom?.fields?.includes(field));

    const formatter = firstMatchingAtom?.formatter;
    const label = firstMatchingAtom?.label;
    const showInParams = firstMatchingAtom?.showInParams ?? true;
    if (formatter) {
      return { value: formatter(value), label, showInParams };
    } else {
      return { value, label, showInParams };
    }
  }
);

// Atom that holds a formatted object for all search fields
const tableFormattedSearchObjectAtom = atom((get) => {
  const listAllAtomIds = get(tableSearchAtomsAtom);
  const searchObject = listAllAtomIds.reduce((acc, fieldValue) => {
    const { search } = fieldValue;
    if (!search) return acc;
    return mergeDeep(acc, search);
  }, {});
  return searchObject as TableSearchObject;
});

tableFormattedSearchObjectAtom.debugLabel = 'tableFormattedSearchObjectAtom';

// Atom that holds a formatted object for all search fields excluding fields that are not shown in params
const tableSearchParamsAtom = atom((get) => {
  const listAllAtomIds = get(tableSearchAtomsAtom);
  const searchObject = listAllAtomIds.reduce((acc, fieldValue) => {
    const { search } = fieldValue;
    if (!search) return acc;
    if (!fieldValue.showInParams) return acc;
    return mergeDeep(acc, search);
  }, {});
  return searchObject as TableSearchObject;
});

tableSearchParamsAtom.debugLabel = 'tableSearchParamsAtom';

export {
  tableFormattedSearchObjectAtom,
  tableSearchAtomFamily,
  tableSearchAtomsAtom,
  tableSearchListAtom,
  tableSearchParamsAtom,
  tableSearchResetAtom,
  tableSearch_InputFormatterAtom,
  tableSearch_InputResetAtom,
  tableSearch_InputResetNotShownInParamsAtom,
};
